/**
 * The one keyboard/visual-viewport contract for every Ember input surface.
 *
 * Three environments answer the software keyboard differently, and only one
 * of them can be told apart up front:
 *
 * - Safari (browser tab): the keyboard OVERLAYS the page. `innerHeight`
 *   stays put, `visualViewport.height` shrinks, and if the focused field
 *   would be covered Safari pans the page (offsetTop), sometimes reporting
 *   the resize and pan in separate animation steps.
 * - iOS standalone PWA (iOS 17+): the keyboard RESIZES the layout viewport —
 *   `innerHeight`, `visualViewport.height` and `100dvh` all drop together,
 *   `interactive-widget` is ignored, and a known WebKit bug can leave the
 *   viewport stuck at the shrunk size after the keyboard dismisses.
 * - Android Chrome: `interactive-widget=resizes-content` (set in our
 *   viewport meta) makes the layout viewport resize cleanly.
 * - Android in-app webviews (Facebook's browser, measured on-device
 *   2026-09-03): the host app never resizes its WebView for the keyboard, so
 *   NOTHING moves — `innerHeight`, `visualViewport.height` and
 *   `interactive-widget` all stay put while the keyboard covers the bottom
 *   of the page. There is no geometry to read at all.
 *
 * So nothing here assumes an environment. The rules are:
 *
 * 1. `--eh-visual-viewport-top` is ALWAYS 0 and `--eh-visual-viewport-height`
 *    is ALWAYS measured `offsetTop + vv.height`: the page's bottom edge sits
 *    at the visual viewport's bottom edge, so the composer hugs the keyboard
 *    in every pan/no-pan combination without ever following a pan (following
 *    made swipes move the page the wrong way). A residual pan left standing
 *    with the keyboard closed is undone once, after the finger lifts.
 * 2. Measured viewport geometry is the only source of truth. We never
 *    pre-shrink from a cached keyboard height: Safari may pan at the same time,
 *    and stacking that pan with a prediction makes the composer ride high and
 *    then snap back when the real measurement arrives.
 * 3. Viewport geometry is never CSS-transitioned. The OS already animates the
 *    keyboard; delaying the matching page height makes the two animations
 *    visibly disagree.
 * 4. In standalone mode, a viewport stuck below its known keyboard-closed
 *    height gets repaired with a display flip + forced reflow.
 * 5. Where the viewport reports nothing (rule 2 has nothing to measure), an
 *    Android in-app webview gets a PHANTOM inset: an editable takes focus
 *    (or an already-focused one is tapped again — Android's back button and
 *    the keyboard's own ⌄ hide the keyboard WITHOUT blurring, and the next
 *    tap brings it back with no focus event), the keyboard's animation
 *    window passes with the viewport unchanged, and a typical Android
 *    keyboard height is reserved from the bottom. It is a guess, so it only
 *    stands while no measurement exists — any real viewport movement takes
 *    over at once — and it is dropped by a blur, by a touch landing where
 *    the keyboard would be (the IME would have swallowed it, so the keyboard
 *    is gone), and by a drag outside the field's zone, which blurs the field
 *    on purpose: scrolling the conversation dismisses the keyboard, the way
 *    it does in Messages, because a hidden keyboard cannot be told from a
 *    shown one here and a drag is the one gesture both states share. While
 *    it stands, body carries `ember-keyboard-guessed` so input surfaces can
 *    pad their bottom edge against the guess running short.
 *
 * A hidden debug HUD (four quick taps in the top-left corner, or ?vvdebug=1)
 * overlays the live numbers so field reports come with data.
 */

import { isInAppWebview } from '../../../../shared-web/ua-detect';

const OPEN_INSET = 120;
const HUD_STORE_KEY = 'eh-vvdebug';
// Rule 5. Android's keyboard show animation is ~250ms; a viewport that has
// not moved by 400ms is not going to. The reserved height is a share of the
// SCREEN (keyboards scale with the screen, not with the webview). Portrait
// keyboards with a toolbar row cover 0.33–0.37 of the screen (Gboard,
// Samsung, Sogou); a number row adds ~0.05. 0.36 sits on the common case:
// Facebook's Android webview on 2026-09-03 measured 282px of keyboard over
// a 690px page on an 800px screen (0.35), and 0.40 there left a gap the
// tester called large. The zone's extra bottom padding (ConversationShell.css)
// is what absorbs a taller keyboard.
const PHANTOM_DELAY = 400;
const PHANTOM_SCREEN_SHARE = 0.36;

type OrientKey = 'p' | 'l';

interface Measured {
  vvH: number;
  vvTop: number;
  innerH: number;
  inset: number;
}

const measure = (): Measured => {
  const vv = window.visualViewport;
  const vvH = Math.round(vv?.height || window.innerHeight || 0);
  const vvTop = Math.round(vv?.offsetTop || 0);
  const innerH = window.innerHeight || vvH;
  // Occlusion is pan-independent (innerH − vvH, no offsetTop term): a user
  // pan while the keyboard is up must not flap the keyboard-open state.
  return { vvH, vvTop, innerH, inset: Math.max(0, innerH - vvH) };
};

const orientKey = (): OrientKey =>
  (window.matchMedia('(orientation: portrait)').matches ? 'p' : 'l');

const isEditable = (el: unknown): el is HTMLElement =>
  el instanceof HTMLTextAreaElement
  || (el instanceof HTMLInputElement
    && !['checkbox', 'radio', 'button', 'submit', 'range', 'file'].includes(el.type));

export function installKeyboardViewportContract(): () => void {
  const standalone = window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as { standalone?: boolean }).standalone === true;
  // EXPERIMENT (Riley, 2026-08-24): on Apple devices do NOTHING — no vars
  // (CSS falls back to 100dvh), no keyboard-open class, no unpan, no repair.
  // The OS's own focus reveal is the only actor, so its raw behavior can be
  // judged on-device. Android and everything else keep the full contract.
  // Only the read-only HUD stays active on iOS.
  const passive = /iP(hone|ad|od)/.test(window.navigator.userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

  // Rule 5 eligibility: Android, inside an in-app browser. Real browsers
  // report the viewport and never earn the guess; iOS webviews sit under the
  // passive experiment above and report the visual viewport themselves.
  const phantomEligible = /Android/i.test(window.navigator.userAgent) && isInAppWebview();
  // The phantom inset standing right now, and the one pending transition
  // (arming after focus, or dropping after blur) — never both at once.
  let phantomInset = 0;
  let phantomTimer = 0;
  let phantomBaseline = 0;
  const guessKeyboardInset = (innerH: number): number => {
    const screenH = window.screen.height || 0;
    const fromScreen = screenH > 0 ? screenH * PHANTOM_SCREEN_SHARE : innerH * 0.45;
    // Never eat more than half the page: a landscape or split-screen webview
    // must keep room to read the field being typed into.
    return Math.round(Math.min(fromScreen, innerH * 0.5));
  };

  let frame = 0;
  let settleTimer = 0;
  let repairTimer = 0;

  // Tallest keyboard-closed visual viewport seen, per orientation: the
  // reference every "how far did the keyboard shrink us" question is asked
  // against, and the target the standalone stuck-viewport repair restores to.
  const maxNoKb: Partial<Record<OrientKey, number>> = {};
  let lastBlurAt = 0;
  let repairAttempts = 0;
  let lastRepairAt = 0;
  let touchCount = 0;
  let panSeen: { top: number; at: number } | null = null;
  let panTimer = 0;
  // A settled pan stops producing events, so re-look shortly after one is seen.
  const armPanCheck = (): void => {
    window.clearTimeout(panTimer);
    panTimer = window.setTimeout(sync, 200);
  };

  // ---- debug HUD -----------------------------------------------------------
  let hudOn = false;
  try {
    hudOn = /[?&]vvdebug/.test(window.location.search)
      || window.sessionStorage.getItem(HUD_STORE_KEY) === '1';
    if (hudOn) window.sessionStorage.setItem(HUD_STORE_KEY, '1');
  } catch { /* storage unavailable — HUD stays off */ }
  let hudEl: HTMLDivElement | null = null;
  const hudLog: string[] = [];
  const log = (msg: string): void => {
    if (!hudOn) return;
    hudLog.push(`${((Date.now() / 1000) % 1000).toFixed(1)} ${msg}`);
    if (hudLog.length > 8) hudLog.shift();
  };
  const renderHud = (m: Measured): void => {
    if (!hudOn) {
      if (hudEl) { hudEl.remove(); hudEl = null; }
      return;
    }
    if (!hudEl) {
      hudEl = document.createElement('div');
      hudEl.style.cssText = 'position:fixed;top:max(2px,env(safe-area-inset-top));left:2px;z-index:2147483647;'
        + 'pointer-events:none;font:10px/1.35 ui-monospace,monospace;color:#fff;'
        + 'background:rgba(0,0,0,0.72);padding:4px 6px;border-radius:6px;white-space:pre;max-width:70vw;';
      document.body.appendChild(hudEl);
    }
    const k = orientKey();
    hudEl.textContent = [
      `kv10${passive ? ' NATIVE(ios untouched)' : ''}  inner ${m.innerH}  vv ${m.vvH}  top ${m.vvTop}  inset ${m.inset}  touch ${touchCount}`,
      `mode ${standalone ? 'standalone' : 'browser'}  max ${maxNoKb[k] ?? '-'}  webview ${phantomEligible ? 'yes' : 'no'}`,
      `pageH ${document.body.style.getPropertyValue('--eh-visual-viewport-height') || '-'}  kb ${document.body.classList.contains('ember-keyboard-open') ? 'open' : 'closed'}  phantom ${phantomInset}`,
      ...hudLog,
    ].join('\n');
  };
  // Four quick taps in the top-left corner toggle the HUD — a standalone PWA
  // has no address bar to type a query flag into.
  let cornerTaps: number[] = [];
  const onCornerTap = (e: TouchEvent | MouseEvent): void => {
    const t = 'touches' in e ? e.touches[0] : e;
    if (!t || t.clientX > 80 || t.clientY > 80) return;
    const now = Date.now();
    cornerTaps = [...cornerTaps.filter((x) => now - x < 1400), now];
    if (cornerTaps.length >= 4) {
      cornerTaps = [];
      hudOn = !hudOn;
      try { window.sessionStorage.setItem(HUD_STORE_KEY, hudOn ? '1' : '0'); } catch { /* fine */ }
      log('hud toggled');
      renderHud(measure());
    }
  };
  // ---------------------------------------------------------------------------

  // The page NEVER follows visualViewport.offsetTop. Following it ("pin the
  // page to the visual viewport") made every OS pan drag the page with the
  // event lag — swiping up visibly moved the page down, and each keyboard
  // pan became a ride-up-then-snap. Pinned surfaces stay at top 0; a pan is
  // undone once, quietly, after the finger lifts (see below).
  const setVars = (height: number): void => {
    if (height > 0) {
      document.body.style.setProperty('--eh-visual-viewport-height', `${height}px`);
    }
    document.body.style.setProperty('--eh-visual-viewport-top', '0px');
  };

  // The standalone stuck-viewport repair (dev.to/cederhook): after the
  // keyboard closes, WebKit can leave innerHeight/visualViewport at the
  // shrunk size for the rest of the session; flipping display with a
  // synchronous reflow in between makes it re-measure.
  const scheduleRepair = (): void => {
    if (repairTimer || Date.now() - lastRepairAt < 1500 || repairAttempts >= 3) return;
    repairTimer = window.setTimeout(() => {
      repairTimer = 0;
      const m = measure();
      const k = orientKey();
      const ceiling = maxNoKb[k];
      const stillStuck = !!ceiling && m.vvH < ceiling - 30 && m.inset <= OPEN_INSET
        && !isEditable(document.activeElement) && Date.now() - lastBlurAt > 500;
      if (!stillStuck) return;
      lastRepairAt = Date.now();
      repairAttempts += 1;
      log(`repair #${repairAttempts} vv ${m.vvH} < ${ceiling}`);
      const prev = document.body.style.display;
      document.body.style.display = 'none';
      void document.body.offsetHeight;
      document.body.style.display = prev;
      if (repairAttempts >= 3) {
        // It will not budge — accept this as the real ceiling so we stop
        // flashing the page and stop mis-measuring future keyboard drops.
        maxNoKb[k] = measure().vvH;
      }
      sync();
    }, 220);
  };

  const sync = (): void => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      const m = measure();
      const k = orientKey();
      const now = Date.now();
      const editableFocused = isEditable(document.activeElement);

      if (passive) {
        renderHud(m);
        return;
      }

      // Reference height: only trusted well clear of any keyboard traffic.
      if (!editableFocused && m.inset <= OPEN_INSET && now - lastBlurAt > 500) {
        if (!maxNoKb[k] || m.vvH > (maxNoKb[k] as number)) {
          maxNoKb[k] = m.vvH;
          repairAttempts = 0;
        } else if (standalone && m.vvH < (maxNoKb[k] as number) - 30) {
          scheduleRepair();
        }
      }

      const ceiling = maxNoKb[k];

      // THE invariant: the page's bottom edge sits at the visual viewport's
      // bottom edge — height = offsetTop + vv.height, top pinned at 0. When
      // iOS pans the viewport to reveal a focused field (a pan it owns and
      // may refuse to give back while the keyboard is up), the page grows by
      // exactly the pan, so the composer stays glued to the keyboard's top
      // in EVERY pan/no-pan combination. Shrinking to vv.height alone while
      // a pan stood stacked the two reliefs and threw the composer to the
      // top of the screen.
      // Rule 5: the phantom stands only while a field is focused AND the
      // viewport still reports nothing — a real inset always wins, and a
      // viewport that moves late (a slow keyboard animation in a webview that
      // does resize) retires the guess rather than stacking on top of it.
      if (phantomInset > 0 && Math.abs(m.vvH - phantomBaseline) >= 30) {
        log(`phantom dropped: vv ${phantomBaseline} -> ${m.vvH}`);
        phantomInset = 0;
      }
      const phantom = editableFocused && m.inset <= OPEN_INSET ? phantomInset : 0;
      setVars(m.vvTop + m.vvH - phantom);
      const kbOpen = phantom > 0
        || m.inset > OPEN_INSET
        || (editableFocused && !!ceiling && m.vvH < ceiling - OPEN_INSET);
      document.body.classList.toggle('ember-keyboard-open', kbOpen);
      document.body.classList.toggle('ember-keyboard-guessed', phantom > 0);

      // Undo a RESIDUAL pan once it has settled — keyboard closed, finger
      // up, nothing focused, document with nothing to scroll. While the
      // keyboard is open the pan is iOS's reveal and is already compensated
      // by the height invariant above; fighting it would just oscillate.
      const scroller = document.scrollingElement;
      const docUnscrollable = !!scroller && scroller.scrollHeight - scroller.clientHeight < 2;
      const scale = window.visualViewport?.scale ?? 1;
      if (!kbOpen && !editableFocused && m.vvTop > 8 && docUnscrollable
        && touchCount === 0 && Math.abs(scale - 1) < 0.02) {
        if (panSeen && Math.abs(panSeen.top - m.vvTop) <= 2) {
          if (now - panSeen.at >= 160) {
            panSeen = null;
            log(`unpan ${m.vvTop}`);
            window.scrollTo(0, 0);
          } else {
            armPanCheck();
          }
        } else {
          panSeen = { top: m.vvTop, at: now };
          armPanCheck();
        }
      } else {
        panSeen = null;
      }

      // iOS can leave the layout viewport scrolled after the keyboard
      // closes. On a document that cannot scroll (the full-viewport input
      // surfaces) that residue shows as a dead band under the composer, so
      // clear it — a genuinely scrollable page is never touched.
      if (!kbOpen && scroller && docUnscrollable && scroller.scrollTop > 0) {
        scroller.scrollTop = 0;
      }

      // Only on a genuinely scrollable page: there the browser's own reveal
      // is document scrolling and this keeps the field readable. A marked
      // full-viewport surface guarantees visibility through its flex layout;
      // calling scrollIntoView there would itself provoke an extra OS pan.
      const active = document.activeElement;
      const viewportLocked = active instanceof HTMLElement
        && !!active.closest('[data-eh-viewport-locked]');
      if (!viewportLocked && !docUnscrollable
        && (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)) {
        const rect = active.getBoundingClientRect();
        const visibleTop = m.vvTop;
        const visibleBottom = m.vvTop + m.vvH - phantom;
        if (phantom > 0 && rect.bottom > visibleBottom - 16) {
          // The browser believes the field is already in view (its viewport
          // never shrank), so scrollIntoView is a no-op here: scroll the
          // document by the overlap ourselves, as far as it will go.
          window.scrollBy({ top: rect.bottom + 16 - visibleBottom, behavior: 'auto' });
        } else if (rect.bottom > visibleBottom - 12 || rect.top < visibleTop + 12) {
          active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        }
      }

      renderHud(m);
    });
  };

  // Rule 5: the keyboard is (about to be) shown for the focused field.
  const armPhantom = (): void => {
    if (!phantomEligible || !isEditable(document.activeElement)) return;
    window.clearTimeout(phantomTimer);
    phantomTimer = 0;
    // Already standing (focus moved straight from one field to another, or
    // a tap inside the field while typing): the keyboard never left.
    if (phantomInset > 0) return;
    // Give the real viewport its animation window first; only a viewport
    // that has not moved at all by then earns the guess.
    phantomBaseline = measure().vvH;
    phantomTimer = window.setTimeout(() => {
      phantomTimer = 0;
      const m = measure();
      const unmoved = m.inset <= OPEN_INSET && Math.abs(m.vvH - phantomBaseline) < 30;
      if (!isEditable(document.activeElement) || !unmoved) return;
      phantomInset = guessKeyboardInset(m.innerH);
      log(`phantom ${phantomInset} (vv ${m.vvH} unmoved)`);
      sync();
    }, PHANTOM_DELAY);
  };

  const onFocusIn = (): void => {
    sync();
    armPhantom();
  };

  // A tap on the field that already has focus fires no focus event, yet it
  // is exactly how the keyboard comes back after Android's back button or
  // the keyboard's ⌄ hid it (neither blurs). Field report 2026-09-03: the
  // composer went under the keyboard on that second showing and stayed.
  const onClick = (e: MouseEvent): void => {
    if (isEditable(e.target) && e.target === document.activeElement) armPhantom();
  };

  const onFocusOut = (): void => {
    lastBlurAt = Date.now();
    if (phantomEligible) {
      window.clearTimeout(phantomTimer);
      // Deferred a tick so a focus landing on another field (focusin fires
      // right after this focusout) can keep the phantom instead of flashing
      // the page to full height and back.
      phantomTimer = window.setTimeout(() => {
        phantomTimer = 0;
        if (phantomInset > 0) log('phantom dropped: blur');
        phantomInset = 0;
        sync();
      }, 0);
    }
    sync();
    // The close animation reports late, and standalone mode may need the
    // stuck-viewport check after it — look again once things settle.
    window.clearTimeout(settleTimer);
    // Reference/repair reads are deliberately gated until 500ms after blur,
    // so this fallback must land beyond that gate when WebKit emits no final
    // viewport event of its own.
    settleTimer = window.setTimeout(sync, 600);
  };

  // Rule 5 drag tracking: where the touch began, and whether it began outside
  // the focused field's zone (the composer on /talk, marked
  // data-eh-keyboard-zone; the field itself elsewhere).
  let dragStartY = 0;
  let dragOutsideZone = false;
  const onTouchStart = (e: TouchEvent): void => {
    touchCount = e.touches.length;
    onCornerTap(e);
    const t = e.touches[0];
    dragOutsideZone = false;
    if (phantomInset <= 0 || !t) return;
    // Android's back button and the keyboard's own dismiss key hide the
    // keyboard without blurring the field, and this webview tells the page
    // nothing about it. A touch landing where the keyboard would be is proof
    // it is gone: the IME would have swallowed that touch. The top fifth of
    // the reserved band is left out — the guess runs taller than a slim
    // keyboard, and a tap in that gap must not throw the composer under a
    // keyboard that is still up.
    if (t.clientY > window.innerHeight - phantomInset * 0.8) {
      log('phantom dropped: touch in band');
      phantomInset = 0;
      sync();
      return;
    }
    const active = document.activeElement;
    const zone = active instanceof HTMLElement ? (active.closest('[data-eh-keyboard-zone]') ?? active) : null;
    dragStartY = t.clientY;
    dragOutsideZone = !!zone && !(e.target instanceof Node && zone.contains(e.target));
  };
  // A drag that began outside the zone — scrolling the conversation — blurs
  // the field: the keyboard (if it was still up) goes, the phantom goes with
  // it, and the page is whole again. A tap outside blurs natively already;
  // this is the scroll, which never would. Deliberate trade: reading back
  // while composing costs a tap on the field to bring the keyboard back,
  // the same as Messages on iOS. Without it a keyboard hidden with ⌄ left
  // the page shrunk until the person happened to touch the empty band.
  const onTouchMove = (e: TouchEvent): void => {
    const t = e.touches[0];
    if (!dragOutsideZone || phantomInset <= 0 || !t) return;
    if (Math.abs(t.clientY - dragStartY) < 12) return;
    dragOutsideZone = false;
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      log('phantom: drag outside zone, blurring');
      active.blur();
    }
  };
  const onTouchEnd = (e: TouchEvent): void => {
    touchCount = e.touches.length;
    if (touchCount === 0) sync();
  };

  sync();
  window.visualViewport?.addEventListener('resize', sync);
  window.visualViewport?.addEventListener('scroll', sync);
  window.addEventListener('resize', sync);
  window.addEventListener('focusin', onFocusIn);
  window.addEventListener('focusout', onFocusOut);
  window.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('touchmove', onTouchMove, { passive: true });
  window.addEventListener('touchend', onTouchEnd, { passive: true });
  window.addEventListener('touchcancel', onTouchEnd, { passive: true });
  window.addEventListener('click', onClick);

  return () => {
    window.cancelAnimationFrame(frame);
    window.clearTimeout(settleTimer);
    window.clearTimeout(repairTimer);
    window.clearTimeout(panTimer);
    window.clearTimeout(phantomTimer);
    window.visualViewport?.removeEventListener('resize', sync);
    window.visualViewport?.removeEventListener('scroll', sync);
    window.removeEventListener('resize', sync);
    window.removeEventListener('focusin', onFocusIn);
    window.removeEventListener('focusout', onFocusOut);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchcancel', onTouchEnd);
    window.removeEventListener('click', onClick);
    hudEl?.remove();
    document.body.classList.remove('ember-keyboard-open');
    document.body.classList.remove('ember-keyboard-guessed');
    document.body.style.removeProperty('--eh-visual-viewport-height');
    document.body.style.removeProperty('--eh-visual-viewport-top');
  };
}
